import { forwardRef } from 'react';

import { cva } from 'cva';
import type { DateValue } from 'react-aria-components/Calendar';
import {
  DateSegment,
  DateInput as RACDateInput,
  DatePicker as RACDatePicker,
  type DatePickerProps as RACDatePickerProps,
} from 'react-aria-components/DatePicker';
import { Dialog } from 'react-aria-components/Dialog';
import {
  FieldError,
  type ValidationResult,
} from 'react-aria-components/FieldError';
import { Group } from 'react-aria-components/Group';

import { Button } from '../button/button';
import { Calendar } from '../calendars/calendar';
import {
  applyDateSeparator,
  useDateFormat,
} from '../date_format_context/date_format_context';
import { Icon } from '../icon/icon';
import { Label } from '../label/label';
import { Popover } from '../popover/popover';
import { Text } from '../text/text';

import styles from './date_field.module.css';

export const dateFieldVariants = cva({
  base: styles.date_field,
  variants: {
    size: { s: styles.size_s, m: styles.size_m, l: styles.size_l },
    readOnly: { true: styles.read_only },
  },
  defaultVariants: { size: 'm' },
});

export type DateFieldProps<T extends DateValue> = RACDatePickerProps<T> & {
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  hideLabel?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  name?: string;
  placeholder?: string;
  size?: 's' | 'm' | 'l';
  tooltip?: React.ReactNode;
};

export const DateField = forwardRef<HTMLDivElement, DateFieldProps<DateValue>>(
  (
    {
      className,
      description,
      errorMessage,
      hideLabel,
      isRequired,
      label,
      size = 'm',
      tooltip,
      ...rest
    },
    ref,
  ) => {
    const classes = dateFieldVariants({
      size,
      readOnly: rest.isReadOnly,
      className,
    });

    const { separator } = useDateFormat();

    const buttonSize = size === 'l' ? 'm' : 's';
    const calendarIconSize = size === 'l' ? 's' : size === 'm' ? 'xs' : '3xs';

    return (
      <RACDatePicker
        ref={ref}
        isRequired={isRequired}
        aria-label={hideLabel ? label : undefined}
        className={classes}
        isReadOnly={rest.isReadOnly}
        {...rest}
      >
        {({ isInvalid }) => (
          <>
            {!hideLabel && (
              <div className={styles.label_tooltip_wrapper}>
                <Label isRequired={isRequired}>{label}</Label>
                {tooltip}
              </div>
            )}

            <Group className={styles.input_group}>
              <RACDateInput className={styles.date_input}>
                {(segment) => (
                  <DateSegment
                    segment={applyDateSeparator(segment, separator)}
                    className={styles.segment}
                  />
                )}
              </RACDateInput>

              <Button
                variant="tertiary"
                size={buttonSize}
                className={styles.calendar_button}
                icon={<Icon name="calendar" size={calendarIconSize} />}
              />
            </Group>

            {isInvalid ? (
              <Text
                slot="errorMessage"
                variant="label"
                elementType="div"
                color="error"
              >
                <FieldError>
                  {typeof errorMessage === 'function'
                    ? (validation: ValidationResult) => errorMessage(validation)
                    : errorMessage}
                </FieldError>
              </Text>
            ) : description ? (
              <Text slot="description" variant="label" elementType="div">
                {description}
              </Text>
            ) : null}

            <Popover placement="top" offset={8}>
              <Dialog className={styles.dialog}>
                <Calendar />
              </Dialog>
            </Popover>
          </>
        )}
      </RACDatePicker>
    );
  },
);
